import { useState, useEffect } from 'react';
import {
  triggerSync,
  triggerFullSync,
  triggerCompanySync,
  triggerResourceSync,
  triggerBackfill,
  checkDuplicates,
  debugQueues,
  getSyncStatus,
  getSystemSettings,
  updateSystemSetting,
  SyncResult,
  FullSyncResult,
  CompanySyncResult,
  ResourceSyncResult,
  BackfillResult,
  DuplicateCheckResult,
  DebugQueuesResult,
  SyncHistoryRecord,
} from '../services/api';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthProvider';
import { IntakeFieldDefinition } from '../services/types';

// Default intake form fields
const DEFAULT_INTAKE_FIELDS: IntakeFieldDefinition[] = [
  { id: 'issueType', label: 'Issue Type', type: 'dropdown', required: true, order: 1, options: ['Computer is slow', 'Cannot connect to network/internet', 'Software not working', 'Printer issue', 'Email issue', 'Password/login issue', 'Hardware problem', 'Other'], isSystemField: true },
  { id: 'description', label: 'Describe your issue', type: 'textarea', required: true, order: 2, placeholder: 'Please describe what is happening...', isSystemField: true },
  { id: 'urgency', label: 'How urgent is this?', type: 'radio', required: true, order: 3, options: ['I cannot work at all', "I can work but it's affecting me", 'Minor inconvenience'] },
  { id: 'canRemote', label: 'Can we remotely connect to your computer?', type: 'radio', required: true, order: 4, options: ['Yes', 'No', 'Not sure'] },
  { id: 'affectedDevice', label: 'Affected Computer', type: 'device_select', required: false, order: 5, helpText: 'Auto-detected or selected from list', isSystemField: true },
];

function IntakeSystemSettings() {
  const [scBaseUrl, setScBaseUrl] = useState('https://umetech.screenconnect.com');
  const [scThumbprint, setScThumbprint] = useState('117968d49a6d3383');
  const [dattoComponentId, setDattoComponentId] = useState('');
  const [secureScanUrl, setSecureScanUrl] = useState('https://umt-securescan-api.azurewebsites.net/api');
  const [emailSubject, setEmailSubject] = useState('Support Request: Ticket #{{ticketNumber}}');
  const [formFields, setFormFields] = useState<IntakeFieldDefinition[]>(DEFAULT_INTAKE_FIELDS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<IntakeFieldDefinition | null>(null);
  const [showAddField, setShowAddField] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const settings = await getSystemSettings();
        if (settings.screenConnectBaseUrl) setScBaseUrl(settings.screenConnectBaseUrl as string);
        if (settings.screenConnectThumbprint) setScThumbprint(settings.screenConnectThumbprint as string);
        if (settings.dattoPortableCollectorComponentId) setDattoComponentId(settings.dattoPortableCollectorComponentId as string);
        if (settings.secureScanApiUrl) setSecureScanUrl(settings.secureScanApiUrl as string);
        if (settings.intakeEmailSubject) setEmailSubject(settings.intakeEmailSubject as string);
        if (settings.intakeFormConfig) {
          const config = settings.intakeFormConfig as { fields: IntakeFieldDefinition[] };
          if (config.fields) setFormFields(config.fields);
        }
      } catch {
        // Use defaults
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleSaveIntegration = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateSystemSetting('screenConnectBaseUrl', scBaseUrl, 'ScreenConnect server URL');
      await updateSystemSetting('screenConnectThumbprint', scThumbprint, 'ScreenConnect thumbprint');
      await updateSystemSetting('dattoPortableCollectorComponentId', dattoComponentId, 'Datto PortableCollector component ID');
      await updateSystemSetting('secureScanApiUrl', secureScanUrl, 'SecureScan API URL');
      await updateSystemSetting('intakeEmailSubject', emailSubject, 'Intake email subject template');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFormConfig = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await updateSystemSetting('intakeFormConfig', { fields: formFields }, 'Intake form field configuration');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const newFields = [...formFields];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newFields.length) return;
    [newFields[index], newFields[swapIndex]] = [newFields[swapIndex], newFields[index]];
    newFields.forEach((f, i) => f.order = i + 1);
    setFormFields(newFields);
  };

  const removeField = (id: string) => {
    setFormFields(prev => prev.filter(f => f.id !== id));
  };

  const addOrUpdateField = (field: IntakeFieldDefinition) => {
    setFormFields(prev => {
      const exists = prev.find(f => f.id === field.id);
      if (exists) {
        return prev.map(f => f.id === field.id ? field : f);
      }
      return [...prev, { ...field, order: prev.length + 1 }];
    });
    setEditingField(null);
    setShowAddField(false);
  };

  if (loading) {
    return (
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Intake System Configuration</h3>
        <div className="text-xs text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg border border-slate-700 p-4 space-y-4">
      <h3 className="text-sm font-semibold text-white">Intake System Configuration</h3>
      <p className="text-xs text-slate-400">Configure the client intake form system, ScreenConnect integration, and form fields.</p>

      {error && <div className="p-2 rounded text-xs bg-red-500/20 border border-red-500/30 text-red-400">{error}</div>}
      {saved && <div className="p-2 rounded text-xs bg-green-500/20 border border-green-500/30 text-green-400">Settings saved</div>}

      {/* Integration Settings */}
      <div className="border border-slate-600 rounded-lg p-3 space-y-3">
        <h4 className="text-xs font-semibold text-slate-300">Integration Settings</h4>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] text-slate-400 mb-1">ScreenConnect Base URL</label>
            <input
              type="text"
              value={scBaseUrl}
              onChange={e => setScBaseUrl(e.target.value)}
              className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600 focus:border-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] text-slate-400 mb-1">ScreenConnect Thumbprint</label>
            <input
              type="text"
              value={scThumbprint}
              onChange={e => setScThumbprint(e.target.value)}
              className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600 focus:border-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] text-slate-400 mb-1">Datto PortableCollector Component ID</label>
            <input
              type="text"
              value={dattoComponentId}
              onChange={e => setDattoComponentId(e.target.value)}
              placeholder="Required for on-demand scans"
              className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600 focus:border-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] text-slate-400 mb-1">SecureScan API URL</label>
            <input
              type="text"
              value={secureScanUrl}
              onChange={e => setSecureScanUrl(e.target.value)}
              className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600 focus:border-blue-500 outline-none"
            />
          </div>
        </div>
        <div>
          <label className="block text-[10px] text-slate-400 mb-1">Email Subject Template</label>
          <input
            type="text"
            value={emailSubject}
            onChange={e => setEmailSubject(e.target.value)}
            className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600 focus:border-blue-500 outline-none"
            placeholder="Supports {{ticketNumber}} and {{companyName}}"
          />
        </div>
        <button
          onClick={handleSaveIntegration}
          disabled={saving}
          className="text-xs font-medium px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white"
        >
          {saving ? 'Saving...' : 'Save Integration Settings'}
        </button>
      </div>

      {/* Form Field Builder */}
      <div className="border border-slate-600 rounded-lg p-3 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-slate-300">Form Fields</h4>
          <div className="flex gap-2">
            <button
              onClick={() => { setFormFields(DEFAULT_INTAKE_FIELDS); }}
              className="text-[10px] px-2 py-1 rounded bg-slate-600 hover:bg-slate-500 text-slate-300"
            >
              Reset to Defaults
            </button>
            <button
              onClick={() => setShowAddField(true)}
              className="text-[10px] px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white"
            >
              + Add Field
            </button>
          </div>
        </div>

        <div className="space-y-1">
          {formFields.sort((a, b) => a.order - b.order).map((field, index) => (
            <div key={field.id} className="flex items-center gap-2 bg-slate-700/50 rounded px-2 py-1.5">
              <div className="flex flex-col gap-0.5">
                <button
                  onClick={() => moveField(index, 'up')}
                  disabled={index === 0}
                  className="text-[10px] text-slate-400 hover:text-white disabled:text-slate-600"
                >
                  &#9650;
                </button>
                <button
                  onClick={() => moveField(index, 'down')}
                  disabled={index === formFields.length - 1}
                  className="text-[10px] text-slate-400 hover:text-white disabled:text-slate-600"
                >
                  &#9660;
                </button>
              </div>
              <span className="text-xs text-white flex-1">{field.label}</span>
              <span className="text-[10px] text-slate-500 px-1.5 py-0.5 bg-slate-600 rounded">{field.type}</span>
              {field.required && <span className="text-[10px] text-red-400">req</span>}
              {field.isSystemField && <span className="text-[10px] text-blue-400">sys</span>}
              <button
                onClick={() => setEditingField(field)}
                className="text-[10px] text-slate-400 hover:text-blue-400"
              >
                Edit
              </button>
              {!field.isSystemField && (
                <button
                  onClick={() => removeField(field.id)}
                  className="text-[10px] text-slate-400 hover:text-red-400"
                >
                  Del
                </button>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={handleSaveFormConfig}
          disabled={saving}
          className="text-xs font-medium px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white"
        >
          {saving ? 'Saving...' : 'Save Form Configuration'}
        </button>
      </div>

      {/* Add/Edit Field Modal */}
      {(showAddField || editingField) && (
        <FieldEditModal
          field={editingField}
          onSave={addOrUpdateField}
          onClose={() => { setEditingField(null); setShowAddField(false); }}
        />
      )}
    </div>
  );
}

function FieldEditModal({
  field,
  onSave,
  onClose,
}: {
  field: IntakeFieldDefinition | null;
  onSave: (f: IntakeFieldDefinition) => void;
  onClose: () => void;
}) {
  const [label, setLabel] = useState(field?.label || '');
  const [type, setType] = useState<IntakeFieldDefinition['type']>(field?.type || 'text');
  const [required, setRequired] = useState(field?.required ?? true);
  const [options, setOptions] = useState(field?.options?.join('\n') || '');
  const [placeholder, setPlaceholder] = useState(field?.placeholder || '');
  const [helpText, setHelpText] = useState(field?.helpText || '');

  const handleSave = () => {
    if (!label.trim()) return;
    const id = field?.id || label.toLowerCase().replace(/[^a-z0-9]+/g, '_');
    const optionsArr = options.split('\n').map(o => o.trim()).filter(Boolean);

    onSave({
      id,
      label: label.trim(),
      type,
      required,
      order: field?.order || 999,
      options: optionsArr.length > 0 ? optionsArr : undefined,
      placeholder: placeholder || undefined,
      helpText: helpText || undefined,
      isSystemField: field?.isSystemField,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-slate-800 rounded-lg border border-slate-600 p-4 w-96 max-w-[90vw]" onClick={e => e.stopPropagation()}>
        <h4 className="text-sm font-semibold text-white mb-3">{field ? 'Edit Field' : 'Add Field'}</h4>

        <div className="space-y-3">
          <div>
            <label className="block text-[10px] text-slate-400 mb-1">Label</label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600"
            />
          </div>
          <div>
            <label className="block text-[10px] text-slate-400 mb-1">Type</label>
            <select
              value={type}
              onChange={e => setType(e.target.value as IntakeFieldDefinition['type'])}
              className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600"
              disabled={field?.isSystemField}
            >
              <option value="text">Text</option>
              <option value="textarea">Textarea</option>
              <option value="dropdown">Dropdown</option>
              <option value="radio">Radio</option>
              <option value="checkbox">Checkbox</option>
            </select>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={required} onChange={e => setRequired(e.target.checked)} className="rounded" />
            <span className="text-xs text-slate-300">Required</span>
          </label>
          {(type === 'dropdown' || type === 'radio') && (
            <div>
              <label className="block text-[10px] text-slate-400 mb-1">Options (one per line)</label>
              <textarea
                value={options}
                onChange={e => setOptions(e.target.value)}
                className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600 min-h-[80px]"
              />
            </div>
          )}
          <div>
            <label className="block text-[10px] text-slate-400 mb-1">Placeholder</label>
            <input
              type="text"
              value={placeholder}
              onChange={e => setPlaceholder(e.target.value)}
              className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600"
            />
          </div>
          <div>
            <label className="block text-[10px] text-slate-400 mb-1">Help Text</label>
            <input
              type="text"
              value={helpText}
              onChange={e => setHelpText(e.target.value)}
              className="w-full bg-slate-700 text-white text-xs px-2 py-1.5 rounded border border-slate-600"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded bg-slate-600 hover:bg-slate-500 text-white">Cancel</button>
          <button onClick={handleSave} disabled={!label.trim()} className="text-xs px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white">Save</button>
        </div>
      </div>
    </div>
  );
}

function MileageSettings() {
  const [rate, setRate] = useState<string>('0.725');
  const [address, setAddress] = useState<string>('28364 Vincent Moraga Dr STE E, Temecula, CA 92590');
  const [coords, setCoords] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [geocoding, setGeocoding] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const settings = await getSystemSettings();
        if (settings.mileageRatePerMile !== undefined) setRate(String(settings.mileageRatePerMile));
        if (settings.officeAddress) setAddress(settings.officeAddress as string);
        if (settings.officeCoordinates) {
          const c = settings.officeCoordinates as { lat: number; lng: number };
          setCoords(`${c.lat}, ${c.lng}`);
        }
      } catch {
        // Use defaults
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const rateNum = parseFloat(rate);
      if (isNaN(rateNum) || rateNum <= 0) {
        setError('Invalid rate');
        setSaving(false);
        return;
      }
      await updateSystemSetting('mileageRatePerMile', rateNum, 'Mileage reimbursement rate per mile');
      await updateSystemSetting('officeAddress', address, 'Office street address');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleGeocode = async () => {
    setGeocoding(true);
    setError(null);
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&countrycodes=us`);
      const data = await res.json() as { lat: string; lon: string }[];
      if (data.length === 0) {
        setError('Address not found');
        setGeocoding(false);
        return;
      }
      const lat = parseFloat(data[0].lat);
      const lng = parseFloat(data[0].lon);
      setCoords(`${lat}, ${lng}`);
      await updateSystemSetting('officeCoordinates', { lat, lng }, 'Pre-geocoded office coordinates');
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Geocoding failed');
    } finally {
      setGeocoding(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs text-slate-400 mb-1">Mileage Rate ($/mile)</label>
        <input
          type="number"
          step="0.001"
          min="0"
          value={rate}
          onChange={(e) => setRate(e.target.value)}
          disabled={loading}
          className="w-32 bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none disabled:opacity-50"
        />
        <span className="text-xs text-slate-500 ml-2">CA rate Jan 2026: $0.725/mi</span>
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Office Address</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            disabled={loading}
            className="flex-1 bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none disabled:opacity-50"
          />
          <button
            onClick={handleGeocode}
            disabled={geocoding || loading}
            className={`px-3 py-2 rounded text-xs font-medium transition-colors ${
              geocoding || loading ? 'bg-slate-600 text-slate-400 cursor-not-allowed' : 'bg-teal-600 hover:bg-teal-700 text-white'
            }`}
          >
            {geocoding ? 'Geocoding...' : 'Re-geocode'}
          </button>
        </div>
        {coords && <div className="text-xs text-slate-500 mt-1">Coordinates: {coords}</div>}
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={saving || loading}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            saving || loading ? 'bg-slate-600 text-slate-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'
          }`}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
        {saved && <span className="text-sm text-green-400">Saved!</span>}
        {error && <span className="text-sm text-red-400">{error}</span>}
      </div>
    </div>
  );
}

export default function SystemSettings() {
  const { user } = useAuth();
  // Sync states
  const [syncingTickets, setSyncingTickets] = useState(false);
  const [syncingCompanies, setSyncingCompanies] = useState(false);
  const [syncingResources, setSyncingResources] = useState(false);
  const [syncingFull, setSyncingFull] = useState(false);
  const [syncingBackfill, setSyncingBackfill] = useState(false);
  const [backfillRange, setBackfillRange] = useState('0-14'); // format: "startDaysAgo-endDaysAgo"
  const [backfillProgress, setBackfillProgress] = useState<string | null>(null); // shows current range being processed

  const [ticketSyncResult, setTicketSyncResult] = useState<SyncResult | null>(null);
  const [companySyncResult, setCompanySyncResult] = useState<CompanySyncResult | null>(null);
  const [resourceSyncResult, setResourceSyncResult] = useState<ResourceSyncResult | null>(null);
  const [fullSyncResult, setFullSyncResult] = useState<FullSyncResult | null>(null);
  const [backfillResult, setBackfillResult] = useState<BackfillResult | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [duplicateCheck, setDuplicateCheck] = useState<DuplicateCheckResult | null>(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [queueDebug, setQueueDebug] = useState<DebugQueuesResult | null>(null);
  const [debuggingQueues, setDebuggingQueues] = useState(false);
  const [queueDebugError, setQueueDebugError] = useState<string | null>(null);

  // Last sync times
  const [lastQuickSync, setLastQuickSync] = useState<SyncHistoryRecord | null>(null);
  const [lastFullSync, setLastFullSync] = useState<SyncHistoryRecord | null>(null);
  const [lastCompanySync, setLastCompanySync] = useState<SyncHistoryRecord | null>(null);
  const [lastTicketSync, setLastTicketSync] = useState<SyncHistoryRecord | null>(null);
  const [lastBackfill, setLastBackfill] = useState<SyncHistoryRecord | null>(null);
  const [lastStaleProcessor, setLastStaleProcessor] = useState<SyncHistoryRecord | null>(null);
  const [lastFCRProcessor, setLastFCRProcessor] = useState<SyncHistoryRecord | null>(null);
  const [lastFCRWithMessages, setLastFCRWithMessages] = useState<SyncHistoryRecord | null>(null);

  // Full sync history
  const [syncHistory, setSyncHistory] = useState<SyncHistoryRecord[]>([]);
  const [showSyncHistory, setShowSyncHistory] = useState(false);

  // Integration settings
  const [timetechEnabled, setTimetechEnabled] = useState<boolean>(true);
  const [timetechBaseUrl, setTimetechBaseUrl] = useState<string>('https://time.umetech.net');
  const [timetechClientMappings, setTimetechClientMappings] = useState<string>('{}');
  const [timetechUserMappings, setTimetechUserMappings] = useState<string>(`{
  "29682886": { "userId": 10, "name": "Jeremiah Goode" },
  "29682885": { "userId": 1, "name": "Ken Umemoto" },
  "29682888": { "userId": 11, "name": "Allen Flock" }
}`);
  const [companyAliases, setCompanyAliases] = useState<string[][]>([]);
  const [aliasInputs, setAliasInputs] = useState<Record<number, string>>({});
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const [settingsSaved, setSettingsSaved] = useState(false);

  // Email notification settings
  const [emailNotifEnabled, setEmailNotifEnabled] = useState(false);
  const [emailNotifCustomerTestMode, setEmailNotifCustomerTestMode] = useState(true);
  const [emailNotifTechTestMode, setEmailNotifTechTestMode] = useState(true);
  const [emailNotifTestRecipient, setEmailNotifTestRecipient] = useState('jgoode@umetech.net');
  const [emailNotifCustomerEvents, setEmailNotifCustomerEvents] = useState<Record<string, boolean>>({
    ticket_assigned: true,
    ticket_in_progress: true,
    ticket_waiting_customer: true,
    ticket_note_added: true,
    ticket_completed: true,
    ticket_created_from_fcr: true,
  });
  const [emailNotifTechEvents, setEmailNotifTechEvents] = useState<Record<string, boolean>>({
    ticket_assigned: true,
    ticket_in_progress: true,
    ticket_waiting_customer: true,
    ticket_completed: true,
    ticket_note_added: true,
    ticket_time_added: true,
  });
  const [emailNotifSaving, setEmailNotifSaving] = useState(false);
  const [emailNotifSaved, setEmailNotifSaved] = useState(false);
  const [emailNotifError, setEmailNotifError] = useState<string | null>(null);

  // Fetch sync status - extracted so it can be called after manual syncs
  const refreshSyncStatus = async () => {
    try {
      // getSyncStatus returns the data array directly (fetchWithAuth unwraps it)
      const syncRecords = await getSyncStatus(500);

      if (Array.isArray(syncRecords) && syncRecords.length > 0) {
        // Store full history for the viewer
        setSyncHistory(syncRecords);

        // Find the most recent sync of each type
        const quickSync = syncRecords.find(r => r.syncType === 'incremental');
        const fullSync = syncRecords.find(r => r.syncType === 'full');
        const companySync = syncRecords.find(r => r.syncType === 'companies');
        const ticketSync = syncRecords.find(r => r.syncType === 'tickets');
        const backfillSync = syncRecords.find(r => r.syncType === 'backfill');
        const staleProcessor = syncRecords.find(r => r.syncType === 'stale-ticket-processing');
        const fcrProcessor = syncRecords.find(r => r.syncType === 'fcr-processing');
        // Find last FCR run that actually processed messages
        const fcrWithMessages = syncRecords.find(r =>
          r.syncType === 'fcr-processing' && (r.counts?.ticketsProcessed || 0) > 0
        );

        if (quickSync) setLastQuickSync(quickSync);
        if (fullSync) setLastFullSync(fullSync);
        if (companySync) setLastCompanySync(companySync);
        if (ticketSync) setLastTicketSync(ticketSync);
        if (backfillSync) setLastBackfill(backfillSync);
        if (staleProcessor) setLastStaleProcessor(staleProcessor);
        if (fcrProcessor) setLastFCRProcessor(fcrProcessor);
        if (fcrWithMessages) setLastFCRWithMessages(fcrWithMessages);
      }
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    }
  };

  // Fetch on mount and refresh every 60 seconds
  useEffect(() => {
    refreshSyncStatus();
    const interval = setInterval(refreshSyncStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  // Load integration settings on mount
  useEffect(() => {
    async function loadSettings() {
      try {
        const settings = await getSystemSettings();
        if (settings.timetechEnabled !== undefined) {
          setTimetechEnabled(settings.timetechEnabled);
        }
        if (settings.timetechBaseUrl) {
          setTimetechBaseUrl(settings.timetechBaseUrl as string);
        }
        if (settings.timetechClientMappings) {
          // Store as JSON string for editing
          const mappings = settings.timetechClientMappings;
          setTimetechClientMappings(
            typeof mappings === 'string' ? mappings : JSON.stringify(mappings, null, 2)
          );
        }
        if (settings.timetechUserMappings) {
          const mappings = settings.timetechUserMappings;
          setTimetechUserMappings(
            typeof mappings === 'string' ? mappings : JSON.stringify(mappings, null, 2)
          );
        }
        if (settings.companyAliases) {
          const aliases = settings.companyAliases;
          if (Array.isArray(aliases)) {
            setCompanyAliases(aliases as string[][]);
          } else if (typeof aliases === 'string') {
            try { setCompanyAliases(JSON.parse(aliases) as string[][]); } catch { /* ignore */ }
          }
        }
        // Email notification settings
        if (settings.emailNotificationsEnabled !== undefined) {
          setEmailNotifEnabled(settings.emailNotificationsEnabled as boolean);
        }
        // Per-audience test modes — fall back to legacy single flag, then to true (safe)
        const legacyTM = settings.emailNotificationsTestMode as boolean | undefined;
        if (settings.emailNotificationsCustomerTestMode !== undefined) {
          setEmailNotifCustomerTestMode(settings.emailNotificationsCustomerTestMode as boolean);
        } else if (legacyTM !== undefined) {
          setEmailNotifCustomerTestMode(legacyTM);
        }
        if (settings.emailNotificationsTechTestMode !== undefined) {
          setEmailNotifTechTestMode(settings.emailNotificationsTechTestMode as boolean);
        } else if (legacyTM !== undefined) {
          setEmailNotifTechTestMode(legacyTM);
        }
        if (settings.emailNotificationsTestRecipient) {
          setEmailNotifTestRecipient(settings.emailNotificationsTestRecipient as string);
        }
        if (settings.emailNotificationsCustomerEvents) {
          setEmailNotifCustomerEvents(settings.emailNotificationsCustomerEvents as Record<string, boolean>);
        }
        if (settings.emailNotificationsTechEvents) {
          setEmailNotifTechEvents(settings.emailNotificationsTechEvents as Record<string, boolean>);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
        // Settings might not exist yet, use defaults
      } finally {
        setSettingsLoading(false);
      }
    }
    loadSettings();
  }, []);

  // Save TimeTech settings
  const handleSaveTimetechSettings = async () => {
    setSettingsSaving(true);
    setSettingsError(null);
    setSettingsSaved(false);

    try {
      // Validate client mappings JSON
      let parsedClientMappings = {};
      if (timetechClientMappings.trim()) {
        try {
          parsedClientMappings = JSON.parse(timetechClientMappings);
        } catch {
          setSettingsError('Invalid JSON in client mappings');
          setSettingsSaving(false);
          return;
        }
      }

      // Validate user mappings JSON
      let parsedUserMappings = {};
      if (timetechUserMappings.trim()) {
        try {
          parsedUserMappings = JSON.parse(timetechUserMappings);
        } catch {
          setSettingsError('Invalid JSON in user mappings');
          setSettingsSaving(false);
          return;
        }
      }

      // Filter out empty alias groups and groups with <2 names
      const cleanedAliases = companyAliases.filter(g => g.length >= 2);

      await updateSystemSetting('timetechEnabled', timetechEnabled, 'Enable/disable TimeTech integration');
      await updateSystemSetting('timetechBaseUrl', timetechBaseUrl, 'TimeTech API base URL');
      await updateSystemSetting('timetechClientMappings', parsedClientMappings, 'AutoTask company ID to TimeTech client ID mapping');
      await updateSystemSetting('timetechUserMappings', parsedUserMappings, 'AutoTask resource ID to TimeTech user ID mapping');
      await updateSystemSetting('companyAliases', cleanedAliases, 'Company name alias groups for Compare view merging');
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : 'Failed to save settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  // Save email notification settings
  const handleSaveEmailNotifSettings = async () => {
    setEmailNotifSaving(true);
    setEmailNotifError(null);
    setEmailNotifSaved(false);
    try {
      await updateSystemSetting('emailNotificationsEnabled', emailNotifEnabled, 'Enable/disable ticket email notifications');
      await updateSystemSetting('emailNotificationsCustomerTestMode', emailNotifCustomerTestMode, 'Route customer emails to assigned tech (or fallback recipient) for preview');
      await updateSystemSetting('emailNotificationsTechTestMode', emailNotifTechTestMode, 'Route tech emails to fallback test recipient instead of actual techs');
      await updateSystemSetting('emailNotificationsTestRecipient', emailNotifTestRecipient, 'Fallback test recipient (used when ticket has no assigned tech, or when tech test mode is on)');
      await updateSystemSetting('emailNotificationsCustomerEvents', emailNotifCustomerEvents, 'Customer-facing email event toggles');
      await updateSystemSetting('emailNotificationsTechEvents', emailNotifTechEvents, 'Tech-facing email event toggles');
      setEmailNotifSaved(true);
      setTimeout(() => setEmailNotifSaved(false), 3000);
    } catch (err) {
      setEmailNotifError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setEmailNotifSaving(false);
    }
  };

  // Format date for display - show actual time
  const formatSyncTime = (dateString: string | undefined): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);

    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Calculate time since last run
  const getTimeSince = (dateString: string | undefined): string => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h ago`;
    if (diffHours > 0) return `${diffHours}h ${diffMins % 60}m ago`;
    return `${diffMins}m ago`;
  };

  // Check if a scheduled processor is overdue
  const isProcessorOverdue = (lastRun: SyncHistoryRecord | null, maxHours: number): boolean => {
    if (!lastRun) return true;
    const date = new Date(lastRun.endTime);
    const now = new Date();
    const diffHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    return diffHours > maxHours;
  };

  // Get friendly name for sync type
  const getSyncTypeLabel = (syncType: string): string => {
    const labels: Record<string, string> = {
      'incremental': 'Quick Sync',
      'full': 'Full Sync',
      'tickets': 'Ticket Sync',
      'companies': 'Company Sync',
      'resources': 'Resource Sync',
      'backfill': 'Backfill',
      'stale-ticket-processing': 'Stale Processor',
      'fcr-processing': 'FCR Processor',
      'stats-generation': 'Stats Generation',
    };
    return labels[syncType] || syncType;
  };

  // Get color for sync type
  const getSyncTypeColor = (syncType: string): string => {
    const colors: Record<string, string> = {
      'incremental': 'text-green-400',
      'full': 'text-blue-400',
      'tickets': 'text-emerald-400',
      'companies': 'text-purple-400',
      'resources': 'text-orange-400',
      'backfill': 'text-amber-400',
      'stale-ticket-processing': 'text-rose-400',
      'fcr-processing': 'text-violet-400',
      'stats-generation': 'text-cyan-400',
    };
    return colors[syncType] || 'text-slate-400';
  };

  // Calculate next scheduled sync time
  const getNextQuickSync = (): string => {
    const now = new Date();
    const next = new Date(now);
    next.setSeconds(0, 0);
    next.setMinutes(next.getMinutes() + 1);
    return next.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const getNextFullSync = (): string => {
    const now = new Date();
    const utcHour = now.getUTCHours();
    const scheduledHours = [6, 10, 14, 18]; // 6AM, 10AM, 2PM, 6PM UTC

    // Find next scheduled hour
    let nextHour = scheduledHours.find(h => h > utcHour);
    const nextDate = new Date(now);

    if (nextHour === undefined) {
      // All today's syncs passed, next is tomorrow at 6AM UTC
      nextHour = 6;
      nextDate.setUTCDate(nextDate.getUTCDate() + 1);
    }

    nextDate.setUTCHours(nextHour, 0, 0, 0);

    return nextDate.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleTicketSync = async () => {
    setSyncingTickets(true);
    setTicketSyncResult(null);
    setSyncError(null);

    try {
      const result = await triggerSync();
      setTicketSyncResult(result);
      refreshSyncStatus(); // Immediately update sync history
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Ticket sync failed');
    } finally {
      setSyncingTickets(false);
    }
  };

  const handleCompanySync = async () => {
    setSyncingCompanies(true);
    setCompanySyncResult(null);
    setSyncError(null);

    try {
      const result = await triggerCompanySync();
      setCompanySyncResult(result);
      refreshSyncStatus(); // Immediately update sync history
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Company sync failed');
    } finally {
      setSyncingCompanies(false);
    }
  };

  const handleResourceSync = async () => {
    setSyncingResources(true);
    setResourceSyncResult(null);
    setSyncError(null);

    try {
      const result = await triggerResourceSync();
      setResourceSyncResult(result);
      refreshSyncStatus(); // Immediately update sync history
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Resource sync failed');
    } finally {
      setSyncingResources(false);
    }
  };

  const handleFullSync = async () => {
    setSyncingFull(true);
    setFullSyncResult(null);
    setSyncError(null);

    try {
      const result = await triggerFullSync();
      setFullSyncResult(result);
      refreshSyncStatus(); // Immediately update sync history
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Full sync failed');
    } finally {
      setSyncingFull(false);
    }
  };

  const handleBackfill = async () => {
    setSyncingBackfill(true);
    setBackfillResult(null);
    setSyncError(null);

    try {
      const [startDaysAgo, endDaysAgo] = backfillRange.split('-').map(Number);
      const result = await triggerBackfill(startDaysAgo, endDaysAgo);
      setBackfillResult(result);
      refreshSyncStatus(); // Immediately update sync history
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Backfill failed');
    } finally {
      setSyncingBackfill(false);
    }
  };

  const handleAutoBackfill90Days = async () => {
    const ranges = ['0-14', '14-30', '30-45', '45-60', '60-67', '67-75', '75-82', '82-90'];
    setSyncingBackfill(true);
    setBackfillResult(null);
    setSyncError(null);

    let totalProcessed = 0;
    let totalCreated = 0;
    let totalUpdated = 0;
    let totalErrors = 0;

    try {
      for (const range of ranges) {
        setBackfillProgress(`Running ${range} days ago...`);
        const [startDaysAgo, endDaysAgo] = range.split('-').map(Number);

        const result = await triggerBackfill(startDaysAgo, endDaysAgo);
        totalProcessed += result.ticketsProcessed;
        totalCreated += result.ticketsCreated;
        totalUpdated += result.ticketsUpdated;
        totalErrors += result.errors;
      }

      setBackfillResult({
        syncId: 'auto-90',
        days: 90,
        dateRange: { start: '90 days ago', end: 'today' },
        ticketsProcessed: totalProcessed,
        ticketsCreated: totalCreated,
        ticketsUpdated: totalUpdated,
        errors: totalErrors,
        durationMs: 0,
        status: totalErrors > 0 ? 'partial' : 'success',
      });
      refreshSyncStatus(); // Immediately update sync history
    } catch (error) {
      setSyncError(error instanceof Error ? error.message : 'Auto backfill failed');
    } finally {
      setSyncingBackfill(false);
      setBackfillProgress(null);
    }
  };

  const handleDuplicateCheck = async () => {
    setCheckingDuplicates(true);
    setDuplicateCheck(null);
    setDuplicateError(null);

    try {
      const result = await checkDuplicates();
      setDuplicateCheck(result);
    } catch (error) {
      setDuplicateError(error instanceof Error ? error.message : 'Duplicate check failed');
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const handleDebugQueues = async () => {
    setDebuggingQueues(true);
    setQueueDebug(null);
    setQueueDebugError(null);

    try {
      const result = await debugQueues();
      setQueueDebug(result);
    } catch (error) {
      setQueueDebugError(error instanceof Error ? error.message : 'Queue debug failed');
    } finally {
      setDebuggingQueues(false);
    }
  };

  const isAnySyncing = syncingTickets || syncingCompanies || syncingResources || syncingFull || syncingBackfill;

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader
        userEmail={user?.email}
        middleContent={
          <div className="text-center">
            <span className="text-sm font-medium text-white">System Settings</span>
            <span className="text-xs text-slate-500 ml-2">Administration</span>
          </div>
        }
      />

      {/* Data Sync */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Data Sync</h3>
        <p className="text-sm text-slate-400 mb-4">
          Sync data from AutoTask to update the local database.
        </p>

        {/* Sync Buttons */}
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={handleFullSync}
            disabled={isAnySyncing}
            className={`font-medium px-4 py-2 rounded-md text-sm transition-colors ${
              isAnySyncing
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            {syncingFull ? 'Syncing...' : 'Full Sync'}
          </button>
          <button
            onClick={handleCompanySync}
            disabled={isAnySyncing}
            className={`font-medium px-4 py-2 rounded-md text-sm transition-colors ${
              isAnySyncing
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            {syncingCompanies ? 'Syncing...' : 'Company Sync'}
          </button>
          <button
            onClick={handleResourceSync}
            disabled={isAnySyncing}
            className={`font-medium px-4 py-2 rounded-md text-sm transition-colors ${
              isAnySyncing
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-orange-600 hover:bg-orange-700 text-white'
            }`}
          >
            {syncingResources ? 'Syncing...' : 'Resource Sync'}
          </button>
          <button
            onClick={handleTicketSync}
            disabled={isAnySyncing}
            className={`font-medium px-4 py-2 rounded-md text-sm transition-colors ${
              isAnySyncing
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {syncingTickets ? 'Syncing...' : 'Ticket Sync'}
          </button>
        </div>

        {/* Sync Button Descriptions */}
        <div className="text-xs text-slate-500 mb-4 space-y-1">
          <div><span className="text-blue-400">Full Sync:</span> Syncs companies first, then tickets (recommended)</div>
          <div><span className="text-purple-400">Company Sync:</span> Only sync company data</div>
          <div><span className="text-orange-400">Resource Sync:</span> Sync technicians/resources from AutoTask</div>
          <div><span className="text-green-400">Ticket Sync:</span> Only sync ticket data</div>
        </div>

        {/* Full Sync Result */}
        {fullSyncResult && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            fullSyncResult.status === 'success'
              ? 'bg-green-900/30 border border-green-700'
              : fullSyncResult.status === 'partial'
              ? 'bg-yellow-900/30 border border-yellow-700'
              : 'bg-red-900/30 border border-red-700'
          }`}>
            <div className="font-medium text-white mb-2">
              {fullSyncResult.status === 'success' ? 'Full Sync Completed' :
               fullSyncResult.status === 'partial' ? 'Full Sync Completed with Errors' : 'Full Sync Failed'}
            </div>
            <div className="space-y-1 text-slate-300">
              <div>Companies processed: <span className="text-purple-400">{fullSyncResult.companiesProcessed}</span></div>
              <div>Tickets processed: <span className="text-white">{fullSyncResult.ticketsProcessed}</span></div>
              <div>Created: <span className="text-green-400">{fullSyncResult.ticketsCreated}</span></div>
              <div>Updated: <span className="text-blue-400">{fullSyncResult.ticketsUpdated}</span></div>
              {fullSyncResult.errors > 0 && (
                <div>Errors: <span className="text-red-400">{fullSyncResult.errors}</span></div>
              )}
              <div className="text-slate-400 text-xs mt-2">
                Duration: {(fullSyncResult.durationMs / 1000).toFixed(1)}s
              </div>
              {/* DEBUG: Show sample ticket info */}
              {fullSyncResult.debug && (
                <div className="mt-3 pt-3 border-t border-slate-600">
                  <div className="text-yellow-400 font-medium mb-1">DEBUG: Queue Info</div>
                  <div className="text-xs font-mono bg-slate-800 p-2 rounded space-y-1">
                    <div>Total tickets: {fullSyncResult.debug.totalTickets}</div>
                    <div className="text-green-400">With queueID: {fullSyncResult.debug.ticketsWithQueue}</div>
                    <div className="text-cyan-400">Queue lookup: {fullSyncResult.debug.queueLookupCount} queues</div>
                    {fullSyncResult.debug.allQueues && fullSyncResult.debug.allQueues.length > 0 && (
                      <div className="max-h-24 overflow-y-auto text-slate-400">
                        {fullSyncResult.debug.allQueues.map(([id, name]) => `${id}→${name}`).join(', ')}
                      </div>
                    )}
                    {fullSyncResult.debug.missingQueueIds && fullSyncResult.debug.missingQueueIds.length > 0 && (
                      <div className="mt-2 text-red-400">
                        MISSING from lookup: {fullSyncResult.debug.missingQueueIds.join(', ')}
                      </div>
                    )}
                    {fullSyncResult.debug.sampleWithQueue && (
                      <div className="mt-2">
                        Test: {fullSyncResult.debug.sampleWithQueue.ticketNumber} → queueID {fullSyncResult.debug.sampleWithQueue.queueID} →{' '}
                        <span className={fullSyncResult.debug.sampleWithQueue.resolvedName ? 'text-green-400' : 'text-red-400'}>
                          {fullSyncResult.debug.sampleWithQueue.resolvedName || 'NOT RESOLVED'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Company Sync Result */}
        {companySyncResult && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            companySyncResult.status === 'success'
              ? 'bg-green-900/30 border border-green-700'
              : companySyncResult.status === 'partial'
              ? 'bg-yellow-900/30 border border-yellow-700'
              : 'bg-red-900/30 border border-red-700'
          }`}>
            <div className="font-medium text-white mb-2">
              {companySyncResult.status === 'success' ? 'Company Sync Completed' :
               companySyncResult.status === 'partial' ? 'Company Sync Completed with Errors' : 'Company Sync Failed'}
            </div>
            <div className="space-y-1 text-slate-300">
              <div>Companies processed: <span className="text-purple-400">{companySyncResult.companiesProcessed}</span></div>
              {companySyncResult.errors > 0 && (
                <div>Errors: <span className="text-red-400">{companySyncResult.errors}</span></div>
              )}
              <div className="text-slate-400 text-xs mt-2">
                Duration: {(companySyncResult.durationMs / 1000).toFixed(1)}s
              </div>
            </div>
          </div>
        )}

        {/* Resource Sync Result */}
        {resourceSyncResult && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            resourceSyncResult.status === 'success'
              ? 'bg-green-900/30 border border-green-700'
              : resourceSyncResult.status === 'partial'
              ? 'bg-yellow-900/30 border border-yellow-700'
              : 'bg-red-900/30 border border-red-700'
          }`}>
            <div className="font-medium text-white mb-2">
              {resourceSyncResult.status === 'success' ? 'Resource Sync Completed' :
               resourceSyncResult.status === 'partial' ? 'Resource Sync Completed with Errors' : 'Resource Sync Failed'}
            </div>
            <div className="space-y-1 text-slate-300">
              <div>Resources processed: <span className="text-orange-400">{resourceSyncResult.resourcesProcessed}</span></div>
              <div>Created: <span className="text-green-400">{resourceSyncResult.resourcesCreated}</span> | Updated: <span className="text-blue-400">{resourceSyncResult.resourcesUpdated}</span></div>
              {resourceSyncResult.errors > 0 && (
                <div>Errors: <span className="text-red-400">{resourceSyncResult.errors}</span></div>
              )}
              <div className="text-slate-400 text-xs mt-2">
                Duration: {(resourceSyncResult.durationMs / 1000).toFixed(1)}s
              </div>
            </div>
          </div>
        )}

        {/* Ticket Sync Result */}
        {ticketSyncResult && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            ticketSyncResult.status === 'success'
              ? 'bg-green-900/30 border border-green-700'
              : ticketSyncResult.status === 'partial'
              ? 'bg-yellow-900/30 border border-yellow-700'
              : 'bg-red-900/30 border border-red-700'
          }`}>
            <div className="font-medium text-white mb-2">
              {ticketSyncResult.status === 'success' ? 'Ticket Sync Completed' :
               ticketSyncResult.status === 'partial' ? 'Ticket Sync Completed with Errors' : 'Ticket Sync Failed'}
            </div>
            <div className="space-y-1 text-slate-300">
              <div>Tickets processed: <span className="text-white">{ticketSyncResult.ticketsProcessed}</span></div>
              <div>Created: <span className="text-green-400">{ticketSyncResult.created}</span></div>
              <div>Updated: <span className="text-blue-400">{ticketSyncResult.updated}</span></div>
              {ticketSyncResult.errors > 0 && (
                <div>Errors: <span className="text-red-400">{ticketSyncResult.errors}</span></div>
              )}
              <div className="text-slate-400 text-xs mt-2">
                Duration: {(ticketSyncResult.durationMs / 1000).toFixed(1)}s
              </div>
            </div>
          </div>
        )}

        {/* Sync Error */}
        {syncError && (
          <div className="mt-4 p-3 rounded-md text-sm bg-red-900/30 border border-red-700">
            <div className="font-medium text-red-400">Sync Error</div>
            <div className="text-slate-300 mt-1">{syncError}</div>
          </div>
        )}
      </div>

      {/* Automatic Sync Schedule */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Automatic Sync Schedule</h3>
        <div className="space-y-3 text-sm">
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 mt-1.5 rounded-full bg-green-500"></div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-medium">Quick Sync</span>
                <span className="text-slate-500">•</span>
                <span className="text-xs">
                  {lastQuickSync ? (
                    <span className={lastQuickSync.status === 'success' ? 'text-green-400' : 'text-yellow-400'}>
                      Last: {formatSyncTime(lastQuickSync.endTime)}
                      {lastQuickSync.status !== 'success' && ` (${lastQuickSync.status})`}
                    </span>
                  ) : (
                    <span className="text-slate-500">No syncs yet</span>
                  )}
                </span>
                <span className="text-slate-500">•</span>
                <span className="text-xs text-cyan-400">Next: {getNextQuickSync()}</span>
              </div>
              <div className="text-slate-400">Every minute - Syncs only NEW tickets for fast monitoring</div>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 mt-1.5 rounded-full bg-blue-500"></div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-medium">Full Ticket Sync</span>
                <span className="text-slate-500">•</span>
                <span className="text-xs">
                  {lastFullSync ? (
                    <span className={lastFullSync.status === 'success' ? 'text-green-400' : 'text-yellow-400'}>
                      Last: {formatSyncTime(lastFullSync.endTime)}
                      {lastFullSync.status !== 'success' && ` (${lastFullSync.status})`}
                    </span>
                  ) : (
                    <span className="text-slate-500">No syncs yet</span>
                  )}
                </span>
                <span className="text-slate-500">•</span>
                <span className="text-xs text-cyan-400">Next: {getNextFullSync()}</span>
              </div>
              <div className="text-slate-400">4x daily (6AM, 10AM, 2PM, 6PM UTC) - Syncs all recent and open tickets</div>
            </div>
          </div>
        </div>

        {/* Manual Sync History */}
        <h4 className="text-xs font-semibold text-slate-400 mt-4 mb-2 uppercase tracking-wide">Manual Sync History</h4>
        <div className="space-y-2 text-sm">
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 mt-1.5 rounded-full bg-purple-500"></div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-medium">Company Sync</span>
                <span className="text-slate-500">•</span>
                <span className="text-xs">
                  {lastCompanySync ? (
                    <span className={lastCompanySync.status === 'success' ? 'text-green-400' : 'text-yellow-400'}>
                      Last: {formatSyncTime(lastCompanySync.endTime)}
                      {lastCompanySync.status !== 'success' && ` (${lastCompanySync.status})`}
                    </span>
                  ) : (
                    <span className="text-slate-500">Never run</span>
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 mt-1.5 rounded-full bg-green-600"></div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-medium">Ticket Sync</span>
                <span className="text-slate-500">•</span>
                <span className="text-xs">
                  {lastTicketSync ? (
                    <span className={lastTicketSync.status === 'success' ? 'text-green-400' : 'text-yellow-400'}>
                      Last: {formatSyncTime(lastTicketSync.endTime)}
                      {lastTicketSync.status !== 'success' && ` (${lastTicketSync.status})`}
                    </span>
                  ) : (
                    <span className="text-slate-500">Never run</span>
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <div className="w-2 h-2 mt-1.5 rounded-full bg-orange-500"></div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-white font-medium">Auto Backfill 90 Days</span>
                <span className="text-slate-500">•</span>
                <span className="text-xs">
                  {lastBackfill ? (
                    <span className={lastBackfill.status === 'success' ? 'text-green-400' : 'text-yellow-400'}>
                      Last: {formatSyncTime(lastBackfill.endTime)}
                      {lastBackfill.status !== 'success' && ` (${lastBackfill.status})`}
                    </span>
                  ) : (
                    <span className="text-slate-500">Never run</span>
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Background Processors */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Background Processors</h3>
        <p className="text-sm text-slate-400 mb-4">
          Automated background tasks running on schedule. All times shown in your local timezone.
        </p>

        <div className="space-y-4 text-sm">
          {/* Stale Ticket Processor */}
          <div className="border border-slate-700 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2 h-2 rounded-full ${
                !lastStaleProcessor ? 'bg-slate-500' :
                isProcessorOverdue(lastStaleProcessor, 10) ? 'bg-red-500 animate-pulse' :
                lastStaleProcessor.status === 'success' ? 'bg-green-500' : 'bg-yellow-500'
              }`}></div>
              <span className="text-white font-medium">Stale Ticket Processor</span>
              <span className="text-xs text-slate-500">Schedule: 3x daily (8AM, 12PM, 4PM EST)</span>
              {isProcessorOverdue(lastStaleProcessor, 10) && (
                <span className="text-xs bg-red-900/50 text-red-400 px-2 py-0.5 rounded">OVERDUE &gt;10h</span>
              )}
            </div>
            <div className="text-xs text-slate-400 mb-2">Escalates stale Waiting Customer / Waiting On Us tickets</div>
            {lastStaleProcessor ? (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs bg-slate-900/50 rounded p-2">
                <div><span className="text-slate-500">Last Run:</span> <span className="text-green-400">{formatSyncTime(lastStaleProcessor.endTime)}</span></div>
                <div><span className="text-slate-500">Time Ago:</span> <span className="text-white">{getTimeSince(lastStaleProcessor.endTime)}</span></div>
                <div><span className="text-slate-500">Status:</span> <span className={lastStaleProcessor.status === 'success' ? 'text-green-400' : 'text-yellow-400'}>{lastStaleProcessor.status}</span></div>
                <div><span className="text-slate-500">Duration:</span> <span className="text-white">{lastStaleProcessor.durationFormatted || `${(lastStaleProcessor.durationMs / 1000).toFixed(1)}s`}</span></div>
                <div><span className="text-slate-500">Tickets Checked:</span> <span className="text-white">{lastStaleProcessor.counts?.ticketsProcessed || 0}</span></div>
                <div><span className="text-slate-500">Initialized:</span> <span className="text-cyan-400">{lastStaleProcessor.counts?.created || 0}</span></div>
                <div><span className="text-slate-500">Flagged/Updated:</span> <span className="text-amber-400">{lastStaleProcessor.counts?.updated || 0}</span></div>
                <div><span className="text-slate-500">Errors:</span> <span className={lastStaleProcessor.counts?.deleted ? 'text-red-400' : 'text-slate-500'}>{lastStaleProcessor.counts?.deleted || 0}</span></div>
                {lastStaleProcessor.notes && (
                  <div className="col-span-2 mt-1 pt-1 border-t border-slate-700">
                    <span className="text-slate-500">Details:</span> <span className="text-slate-400">{lastStaleProcessor.notes}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-red-400 bg-slate-900/50 rounded p-2">Never run - processor has not executed yet</div>
            )}
          </div>

          {/* FCR Processor */}
          <div className="border border-slate-700 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2 h-2 rounded-full ${
                !lastFCRProcessor ? 'bg-slate-500' :
                isProcessorOverdue(lastFCRProcessor, 0.1) ? 'bg-red-500 animate-pulse' :
                lastFCRProcessor.status === 'success' ? 'bg-green-500' : 'bg-yellow-500'
              }`}></div>
              <span className="text-white font-medium">FCR Processor</span>
              <span className="text-xs text-slate-500">Schedule: Every 2 minutes</span>
              {isProcessorOverdue(lastFCRProcessor, 0.1) && (
                <span className="text-xs bg-red-900/50 text-red-400 px-2 py-0.5 rounded">OVERDUE &gt;6min</span>
              )}
            </div>
            <div className="text-xs text-slate-400 mb-2">Polls fcr@umetech.net mailbox for forwarded customer requests</div>
            {lastFCRProcessor ? (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs bg-slate-900/50 rounded p-2">
                <div><span className="text-slate-500">Last Checked:</span> <span className="text-green-400">{formatSyncTime(lastFCRProcessor.endTime)}</span></div>
                <div><span className="text-slate-500">Time Ago:</span> <span className="text-white">{getTimeSince(lastFCRProcessor.endTime)}</span></div>
                <div><span className="text-slate-500">Status:</span> <span className={lastFCRProcessor.status === 'success' ? 'text-green-400' : 'text-yellow-400'}>{lastFCRProcessor.status}</span></div>
                <div><span className="text-slate-500">Duration:</span> <span className="text-white">{lastFCRProcessor.durationFormatted || `${(lastFCRProcessor.durationMs / 1000).toFixed(1)}s`}</span></div>
                <div><span className="text-slate-500">Emails Found:</span> <span className="text-white">{lastFCRProcessor.counts?.ticketsProcessed || 0}</span></div>
                <div><span className="text-slate-500">FCRs Created:</span> <span className="text-cyan-400">{lastFCRProcessor.counts?.created || 0}</span></div>
                {lastFCRWithMessages && lastFCRWithMessages.id !== lastFCRProcessor.id && (
                  <div className="col-span-2 mt-1 pt-1 border-t border-slate-700">
                    <span className="text-slate-500">Last Processed Messages:</span>{' '}
                    <span className="text-violet-400">{formatSyncTime(lastFCRWithMessages.endTime)}</span>
                    <span className="text-slate-500"> ({lastFCRWithMessages.counts?.ticketsProcessed} emails → {lastFCRWithMessages.counts?.created} FCRs)</span>
                  </div>
                )}
                {lastFCRProcessor.notes && (
                  <div className="col-span-2 mt-1 pt-1 border-t border-slate-700">
                    <span className="text-slate-500">Details:</span> <span className="text-slate-400">{lastFCRProcessor.notes}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-red-400 bg-slate-900/50 rounded p-2">Never run - processor has not executed yet</div>
            )}
          </div>

          {/* Quick Sync */}
          <div className="border border-slate-700 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2 h-2 rounded-full ${
                !lastQuickSync ? 'bg-slate-500' :
                isProcessorOverdue(lastQuickSync, 0.05) ? 'bg-red-500 animate-pulse' :
                lastQuickSync.status === 'success' ? 'bg-green-500' : 'bg-yellow-500'
              }`}></div>
              <span className="text-white font-medium">Quick Sync</span>
              <span className="text-xs text-slate-500">Schedule: Every minute</span>
              {isProcessorOverdue(lastQuickSync, 0.05) && (
                <span className="text-xs bg-red-900/50 text-red-400 px-2 py-0.5 rounded">OVERDUE &gt;3min</span>
              )}
            </div>
            <div className="text-xs text-slate-400 mb-2">Syncs only NEW tickets from AutoTask for fast monitoring</div>
            {lastQuickSync ? (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs bg-slate-900/50 rounded p-2">
                <div><span className="text-slate-500">Last Run:</span> <span className="text-green-400">{formatSyncTime(lastQuickSync.endTime)}</span></div>
                <div><span className="text-slate-500">Time Ago:</span> <span className="text-white">{getTimeSince(lastQuickSync.endTime)}</span></div>
                <div><span className="text-slate-500">Status:</span> <span className={lastQuickSync.status === 'success' ? 'text-green-400' : 'text-yellow-400'}>{lastQuickSync.status}</span></div>
                <div><span className="text-slate-500">Duration:</span> <span className="text-white">{lastQuickSync.durationFormatted || `${(lastQuickSync.durationMs / 1000).toFixed(1)}s`}</span></div>
                <div><span className="text-slate-500">Tickets Checked:</span> <span className="text-white">{lastQuickSync.counts?.ticketsProcessed || 0}</span></div>
                <div><span className="text-slate-500">Created:</span> <span className="text-cyan-400">{lastQuickSync.counts?.created || 0}</span></div>
                <div><span className="text-slate-500">Updated:</span> <span className="text-amber-400">{lastQuickSync.counts?.updated || 0}</span></div>
                <div><span className="text-slate-500">Errors:</span> <span className={lastQuickSync.counts?.deleted ? 'text-red-400' : 'text-slate-500'}>{lastQuickSync.counts?.deleted || 0}</span></div>
              </div>
            ) : (
              <div className="text-xs text-red-400 bg-slate-900/50 rounded p-2">Never run - processor has not executed yet</div>
            )}
          </div>

          {/* Full Ticket Sync */}
          <div className="border border-slate-700 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2 h-2 rounded-full ${
                !lastFullSync ? 'bg-slate-500' :
                isProcessorOverdue(lastFullSync, 8) ? 'bg-red-500 animate-pulse' :
                lastFullSync.status === 'success' ? 'bg-green-500' : 'bg-yellow-500'
              }`}></div>
              <span className="text-white font-medium">Full Ticket Sync</span>
              <span className="text-xs text-slate-500">Schedule: 4x daily (6AM, 10AM, 2PM, 6PM UTC)</span>
              {isProcessorOverdue(lastFullSync, 8) && (
                <span className="text-xs bg-red-900/50 text-red-400 px-2 py-0.5 rounded">OVERDUE &gt;8h</span>
              )}
            </div>
            <div className="text-xs text-slate-400 mb-2">Syncs all recent and open tickets from AutoTask</div>
            {lastFullSync ? (
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs bg-slate-900/50 rounded p-2">
                <div><span className="text-slate-500">Last Run:</span> <span className="text-green-400">{formatSyncTime(lastFullSync.endTime)}</span></div>
                <div><span className="text-slate-500">Time Ago:</span> <span className="text-white">{getTimeSince(lastFullSync.endTime)}</span></div>
                <div><span className="text-slate-500">Status:</span> <span className={lastFullSync.status === 'success' ? 'text-green-400' : 'text-yellow-400'}>{lastFullSync.status}</span></div>
                <div><span className="text-slate-500">Duration:</span> <span className="text-white">{lastFullSync.durationFormatted || `${(lastFullSync.durationMs / 1000).toFixed(1)}s`}</span></div>
                <div><span className="text-slate-500">Tickets Processed:</span> <span className="text-white">{lastFullSync.counts?.ticketsProcessed || 0}</span></div>
                <div><span className="text-slate-500">Companies:</span> <span className="text-purple-400">{lastFullSync.counts?.companiesProcessed || 0}</span></div>
                <div><span className="text-slate-500">Created:</span> <span className="text-cyan-400">{lastFullSync.counts?.created || 0}</span></div>
                <div><span className="text-slate-500">Updated:</span> <span className="text-amber-400">{lastFullSync.counts?.updated || 0}</span></div>
              </div>
            ) : (
              <div className="text-xs text-yellow-400 bg-slate-900/50 rounded p-2">Never run - waiting for next scheduled time</div>
            )}
          </div>
        </div>
      </div>

      {/* Sync History */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">Sync History</h3>
          <button
            onClick={() => setShowSyncHistory(!showSyncHistory)}
            className="text-xs text-blue-400 hover:text-blue-300"
          >
            {showSyncHistory ? 'Hide' : 'Show'} ({syncHistory.length} records)
          </button>
        </div>

        {showSyncHistory && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-400 border-b border-slate-700">
                  <th className="text-left py-2 pr-4">Type</th>
                  <th className="text-left py-2 pr-4">Time</th>
                  <th className="text-left py-2 pr-4">Status</th>
                  <th className="text-left py-2 pr-4">Duration</th>
                  <th className="text-left py-2 pr-4">Processed</th>
                  <th className="text-left py-2">Trigger</th>
                </tr>
              </thead>
              <tbody>
                {syncHistory.slice(0, 100).map((record) => (
                  <tr key={record.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                    <td className={`py-2 pr-4 font-medium ${getSyncTypeColor(record.syncType)}`}>
                      {getSyncTypeLabel(record.syncType)}
                    </td>
                    <td className="py-2 pr-4 text-slate-300">
                      {formatSyncTime(record.endTime)}
                    </td>
                    <td className="py-2 pr-4">
                      <span className={`px-1.5 py-0.5 rounded text-xs ${
                        record.status === 'success' ? 'bg-green-900/50 text-green-400' :
                        record.status === 'partial' ? 'bg-yellow-900/50 text-yellow-400' :
                        'bg-red-900/50 text-red-400'
                      }`}>
                        {record.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-slate-400">
                      {record.durationFormatted || `${(record.durationMs / 1000).toFixed(1)}s`}
                    </td>
                    <td className="py-2 pr-4 text-slate-300">
                      {record.counts?.ticketsProcessed || record.counts?.companiesProcessed || record.counts?.resourcesProcessed || 0}
                      {record.counts?.created ? ` (+${record.counts.created})` : ''}
                      {record.counts?.updated ? ` (~${record.counts.updated})` : ''}
                    </td>
                    <td className="py-2 text-slate-500">
                      {record.triggeredBy}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {syncHistory.length === 0 && (
              <div className="text-center py-4 text-slate-500">No sync history records found</div>
            )}
          </div>
        )}
      </div>

      {/* Historical Data Backfill */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Historical Data Backfill</h3>
        <p className="text-sm text-slate-400 mb-4">
          One-time import of historical tickets from AutoTask. Use this to populate the database with past tickets.
          Run multiple times with different ranges to avoid timeouts. Start with 30 days.
        </p>

        {/* Auto 90-day button */}
        <button
          onClick={handleAutoBackfill90Days}
          disabled={isAnySyncing}
          className={`font-medium px-4 py-2 rounded-md text-sm transition-colors mb-3 ${
            isAnySyncing
              ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
              : 'bg-orange-600 hover:bg-orange-700 text-white'
          }`}
        >
          {syncingBackfill ? (backfillProgress || 'Backfilling...') : 'Auto Backfill 90 Days'}
        </button>

        {/* Manual range selector */}
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs text-slate-500">Or manual:</span>
          <select
            value={backfillRange}
            onChange={(e) => setBackfillRange(e.target.value)}
            disabled={isAnySyncing}
            className="bg-slate-700 border border-slate-600 rounded-md px-2 py-1 text-white text-xs focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="0-14">0-14 days</option>
            <option value="14-30">14-30 days</option>
            <option value="30-45">30-45 days</option>
            <option value="45-60">45-60 days</option>
            <option value="60-67">60-67 days</option>
            <option value="67-75">67-75 days</option>
            <option value="75-82">75-82 days</option>
            <option value="82-90">82-90 days</option>
          </select>
          <button
            onClick={handleBackfill}
            disabled={isAnySyncing}
            className={`font-medium px-3 py-1 rounded-md text-xs transition-colors ${
              isAnySyncing
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-slate-600 hover:bg-slate-500 text-white'
            }`}
          >
            Run
          </button>
        </div>

        {/* Backfill Result */}
        {backfillResult && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            backfillResult.status === 'success'
              ? 'bg-green-900/30 border border-green-700'
              : backfillResult.status === 'partial'
              ? 'bg-yellow-900/30 border border-yellow-700'
              : 'bg-red-900/30 border border-red-700'
          }`}>
            <div className="font-medium text-white mb-2">
              {backfillResult.status === 'success' ? 'Backfill Completed' :
               backfillResult.status === 'partial' ? 'Backfill Completed with Errors' : 'Backfill Failed'}
            </div>
            <div className="space-y-1 text-slate-300">
              <div>Date range: <span className="text-slate-400">{backfillResult.dateRange.start} to {backfillResult.dateRange.end}</span></div>
              <div>Tickets processed: <span className="text-white">{backfillResult.ticketsProcessed}</span></div>
              <div>Created: <span className="text-green-400">{backfillResult.ticketsCreated}</span></div>
              <div>Updated: <span className="text-blue-400">{backfillResult.ticketsUpdated}</span></div>
              {backfillResult.errors > 0 && (
                <div>Errors: <span className="text-red-400">{backfillResult.errors}</span></div>
              )}
              <div className="text-slate-400 text-xs mt-2">
                Duration: {(backfillResult.durationMs / 1000).toFixed(1)}s
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Database Diagnostics */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Database Diagnostics</h3>
        <p className="text-sm text-slate-400 mb-4">
          Check for data issues in the local database.
        </p>

        <div className="flex gap-3 flex-wrap">
          <button
            onClick={handleDuplicateCheck}
            disabled={checkingDuplicates}
            className={`font-medium px-4 py-2 rounded-md text-sm transition-colors ${
              checkingDuplicates
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-slate-600 hover:bg-slate-500 text-white'
            }`}
          >
            {checkingDuplicates ? 'Checking...' : 'Check for Duplicates'}
          </button>

          <button
            onClick={handleDebugQueues}
            disabled={debuggingQueues}
            className={`font-medium px-4 py-2 rounded-md text-sm transition-colors ${
              debuggingQueues
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-cyan-600 hover:bg-cyan-700 text-white'
            }`}
          >
            {debuggingQueues ? 'Fetching...' : 'Debug Queues'}
          </button>
        </div>

        {/* Duplicate Check Result */}
        {duplicateCheck && (
          <div className={`mt-4 p-3 rounded-md text-sm ${
            duplicateCheck.duplicateCount === 0
              ? 'bg-green-900/30 border border-green-700'
              : 'bg-yellow-900/30 border border-yellow-700'
          }`}>
            <div className="font-medium text-white mb-2">
              {duplicateCheck.duplicateCount === 0 ? 'No Duplicates Found' : `Found ${duplicateCheck.duplicateCount} Duplicate Ticket Numbers`}
            </div>
            <div className="space-y-1 text-slate-300">
              <div>Total unique ticket numbers: <span className="text-white">{duplicateCheck.totalUniqueTicketNumbers}</span></div>
              {duplicateCheck.duplicateCount > 0 && (
                <div className="mt-2">
                  <div className="text-yellow-400 font-medium mb-1">Duplicates (showing first 50):</div>
                  <div className="max-h-40 overflow-y-auto bg-slate-900 rounded p-2 text-xs font-mono">
                    {duplicateCheck.duplicates.map((dup, i) => (
                      <div key={i} className="text-slate-300">
                        {dup.ticketNumber} <span className="text-yellow-400">({dup.count} copies)</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Duplicate Check Error */}
        {duplicateError && (
          <div className="mt-4 p-3 rounded-md text-sm bg-red-900/30 border border-red-700">
            <div className="font-medium text-red-400">Check Failed</div>
            <div className="text-slate-300 mt-1">{duplicateError}</div>
          </div>
        )}

        {/* Queue Debug Result */}
        {queueDebug && (
          <div className="mt-4 p-3 rounded-md text-sm bg-cyan-900/30 border border-cyan-700">
            <div className="font-medium text-white mb-2">Queue Debug Results</div>
            <div className="space-y-1 text-slate-300">
              <div>Ticket Queues (from queueID picklist): <span className="text-cyan-400">{queueDebug.queueCount}</span></div>
              <div>Lookup entries created: <span className="text-cyan-400">{queueDebug.lookupCount}</span></div>
              {queueDebug.queues && queueDebug.queues.length > 0 && (
                <div className="mt-2">
                  <div className="text-cyan-400 font-medium mb-1">Ticket Queues (these are used for tickets):</div>
                  <div className="max-h-48 overflow-y-auto bg-slate-900 rounded p-2 text-xs font-mono">
                    {queueDebug.queues.map((queue, i) => (
                      <div key={i} className="text-slate-300">
                        <span className="text-yellow-400">{queue.id}</span> → <span className="text-green-400">{queue.name}</span>
                        {queue.isActive === false && <span className="text-red-400 ml-2">(inactive)</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {queueDebug.departments && queueDebug.departments.length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <div className="text-slate-500 font-medium mb-1">Departments (NOT ticket queues - different entity):</div>
                  <div className="text-xs text-slate-500 bg-slate-900 rounded p-2 font-mono">
                    {queueDebug.departments.map((dept, i) => (
                      <div key={i}>{dept.id} → {dept.name}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Queue Debug Error */}
        {queueDebugError && (
          <div className="mt-4 p-3 rounded-md text-sm bg-red-900/30 border border-red-700">
            <div className="font-medium text-red-400">Queue Debug Failed</div>
            <div className="text-slate-300 mt-1">{queueDebugError}</div>
          </div>
        )}
      </div>

      {/* Mileage & Expenses */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Mileage & Expenses</h3>
        <p className="text-sm text-slate-400 mb-4">
          Configure mileage reimbursement rate and office address for distance estimates.
        </p>

        <MileageSettings />
      </div>

      {/* Integrations */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Integrations</h3>

        {/* TimeTech Integration */}
        <div className="border border-slate-600 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="text-sm font-medium text-white">TimeTech Integration</h4>
              <p className="text-xs text-slate-400 mt-1">
                Dual-write time entries to both AutoTask and TimeTech
              </p>
            </div>
            {settingsLoading ? (
              <div className="w-10 h-5 bg-slate-600 rounded-full animate-pulse"></div>
            ) : (
              <button
                onClick={() => setTimetechEnabled(!timetechEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  timetechEnabled ? 'bg-green-600' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    timetechEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            )}
          </div>

          {/* Base URL */}
          <div className="mt-3">
            <label className="block text-xs text-slate-400 mb-1">TimeTech Base URL</label>
            <input
              type="text"
              value={timetechBaseUrl}
              onChange={(e) => setTimetechBaseUrl(e.target.value)}
              disabled={settingsLoading}
              placeholder="https://time.umetech.net"
              className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none disabled:opacity-50"
            />
          </div>

          {/* Client Mappings */}
          <div className="mt-3">
            <label className="block text-xs text-slate-400 mb-1">
              Client Mappings (AutoTask Company ID → TimeTech Client ID)
            </label>
            <textarea
              value={timetechClientMappings}
              onChange={(e) => setTimetechClientMappings(e.target.value)}
              disabled={settingsLoading}
              placeholder='{"12345": 67, "12346": 68}'
              rows={4}
              className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none disabled:opacity-50 font-mono"
            />
            <p className="text-xs text-slate-500 mt-1">
              JSON: AutoTask company ID → TimeTech client ID
            </p>
          </div>

          {/* User/Tech Mappings */}
          <div className="mt-3">
            <label className="block text-xs text-slate-400 mb-1">
              Tech Mappings (AutoTask Resource ID → TimeTech User)
            </label>
            <textarea
              value={timetechUserMappings}
              onChange={(e) => setTimetechUserMappings(e.target.value)}
              disabled={settingsLoading}
              placeholder='{"29682886": {"userId": 10, "name": "Jeremiah Goode"}}'
              rows={5}
              className="w-full bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-2 focus:border-blue-500 focus:outline-none disabled:opacity-50 font-mono"
            />
            <p className="text-xs text-slate-500 mt-1">
              JSON: AutoTask resource ID → {`{ userId: number, name: string }`}
            </p>
          </div>

          {/* Company Name Aliases */}
          <div className="mt-3">
            <label className="block text-xs text-slate-400 mb-2">
              Company Name Aliases (for Compare view merging)
            </label>
            <div className="space-y-2">
              {companyAliases.map((group, groupIdx) => (
                <div key={groupIdx} className="bg-slate-700/50 rounded px-3 py-2">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <div className="flex flex-wrap gap-1 mb-2">
                        {group.map((name, nameIdx) => (
                          <span key={nameIdx} className="inline-flex items-center gap-1 bg-slate-600 text-slate-200 text-xs px-2 py-0.5 rounded">
                            {name}
                            <button
                              type="button"
                              onClick={() => {
                                const newAliases = companyAliases.map((g, gi) =>
                                  gi === groupIdx ? g.filter((_, ni) => ni !== nameIdx) : g
                                ).filter(g => g.length >= 2);
                                setCompanyAliases(newAliases);
                              }}
                              className="text-slate-400 hover:text-red-400 ml-0.5"
                              title="Remove name"
                            >
                              &times;
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="flex gap-1">
                        <input
                          type="text"
                          value={aliasInputs[groupIdx] || ''}
                          onChange={(e) => setAliasInputs(prev => ({ ...prev, [groupIdx]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const val = (aliasInputs[groupIdx] || '').trim();
                              if (val && !group.includes(val)) {
                                setCompanyAliases(prev => prev.map((g, gi) => gi === groupIdx ? [...g, val] : g));
                                setAliasInputs(prev => ({ ...prev, [groupIdx]: '' }));
                              }
                            }
                          }}
                          placeholder="Add name variant..."
                          className="flex-1 bg-slate-700 border border-slate-600 text-white text-xs rounded px-2 py-1 focus:border-blue-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const val = (aliasInputs[groupIdx] || '').trim();
                            if (val && !group.includes(val)) {
                              setCompanyAliases(prev => prev.map((g, gi) => gi === groupIdx ? [...g, val] : g));
                              setAliasInputs(prev => ({ ...prev, [groupIdx]: '' }));
                            }
                          }}
                          className="px-2 py-1 bg-slate-600 hover:bg-slate-500 text-slate-300 text-xs rounded"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCompanyAliases(prev => prev.filter((_, gi) => gi !== groupIdx))}
                      className="text-slate-500 hover:text-red-400 p-1 mt-0.5"
                      title="Delete alias group"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setCompanyAliases(prev => [...prev, []])}
              className="mt-2 text-xs text-blue-400 hover:text-blue-300"
            >
              + Add Alias Group
            </button>
            <p className="text-xs text-slate-500 mt-1">
              Names within each group are treated as the same company in the Compare view.
            </p>
          </div>

          {/* Save Button */}
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleSaveTimetechSettings}
              disabled={settingsSaving || settingsLoading}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                settingsSaving || settingsLoading
                  ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {settingsSaving ? 'Saving...' : 'Save Settings'}
            </button>

            {settingsSaved && (
              <span className="text-sm text-green-400">Saved!</span>
            )}

            {settingsError && (
              <span className="text-sm text-red-400">{settingsError}</span>
            )}
          </div>

          {/* Status */}
          <div className="mt-3 text-xs text-slate-500">
            Status: {timetechEnabled ? (
              <span className="text-green-400">Enabled - time entries will sync to TimeTech</span>
            ) : (
              <span className="text-yellow-400">Disabled - time entries will only go to AutoTask</span>
            )}
          </div>
        </div>
      </div>

      {/* Email Notifications */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">Email Notifications</h3>

        <div className="border border-slate-600 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="text-sm font-medium text-white">Ticket Lifecycle Emails</h4>
              <p className="text-xs text-slate-400 mt-1">
                Send branded Umetech emails for ticket status changes, notes, and time entries
              </p>
            </div>
            {settingsLoading ? (
              <div className="w-10 h-5 bg-slate-600 rounded-full animate-pulse"></div>
            ) : (
              <button
                onClick={() => setEmailNotifEnabled(!emailNotifEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  emailNotifEnabled ? 'bg-green-600' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    emailNotifEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            )}
          </div>

          {/* Customer Test Mode */}
          <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-amber-300">Customer Emails — Test Mode</p>
                <p className="text-[10px] text-amber-400/70 mt-0.5">
                  When on, customer emails route to the assigned tech (or fallback recipient) with a [TEST] banner instead of the actual customer.
                </p>
              </div>
              <button
                onClick={() => setEmailNotifCustomerTestMode(!emailNotifCustomerTestMode)}
                disabled={settingsLoading}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  emailNotifCustomerTestMode ? 'bg-amber-500' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    emailNotifCustomerTestMode ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Tech Test Mode */}
          <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-amber-300">Tech Emails — Test Mode</p>
                <p className="text-[10px] text-amber-400/70 mt-0.5">
                  When on, tech emails route to the fallback recipient with a [TEST] banner instead of the actual tech.
                </p>
              </div>
              <button
                onClick={() => setEmailNotifTechTestMode(!emailNotifTechTestMode)}
                disabled={settingsLoading}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  emailNotifTechTestMode ? 'bg-amber-500' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    emailNotifTechTestMode ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Fallback Recipient (used by either test mode when needed) */}
          {(emailNotifCustomerTestMode || emailNotifTechTestMode) && (
            <div className="mt-2 p-3 bg-slate-700/40 border border-slate-600 rounded-lg">
              <label className="block text-[10px] text-slate-400 mb-1">Fallback Recipient</label>
              <p className="text-[10px] text-slate-500 mb-1">
                Used for tech test mode and for customer test mode when the ticket has no assigned tech.
              </p>
              <input
                type="email"
                value={emailNotifTestRecipient}
                onChange={(e) => setEmailNotifTestRecipient(e.target.value)}
                disabled={settingsLoading}
                className="w-full bg-slate-700 border border-slate-600 text-white text-xs rounded px-2 py-1.5 focus:border-amber-500 focus:outline-none disabled:opacity-50"
              />
            </div>
          )}

          {/* Customer Event Toggles */}
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-300 mb-2">Customer-Facing Emails</p>
            <div className="space-y-1.5">
              {[
                { key: 'ticket_assigned', label: 'Ticket Assigned' },
                { key: 'ticket_in_progress', label: 'Status → In Progress' },
                { key: 'ticket_waiting_customer', label: 'Status → Waiting Customer (Action Required)' },
                { key: 'ticket_note_added', label: 'Customer-Visible Note Added' },
                { key: 'ticket_completed', label: 'Ticket Resolved' },
                { key: 'ticket_created_from_fcr', label: 'FCR Acknowledgment (forwarded email → ticket)' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`cust-${key}`}
                    checked={emailNotifCustomerEvents[key] !== false}
                    onChange={(e) => setEmailNotifCustomerEvents(prev => ({ ...prev, [key]: e.target.checked }))}
                    disabled={settingsLoading}
                    className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                  />
                  <label htmlFor={`cust-${key}`} className="text-xs text-slate-400">{label}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Tech Event Toggles */}
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-300 mb-2">Tech-Facing Emails</p>
            <div className="space-y-1.5">
              {[
                { key: 'ticket_assigned', label: 'Ticket Assigned to Me' },
                { key: 'ticket_in_progress', label: 'Status → In Progress' },
                { key: 'ticket_waiting_customer', label: 'Status → Waiting Customer' },
                { key: 'ticket_completed', label: 'Ticket Completed' },
                { key: 'ticket_note_added', label: 'Note Added on My Ticket' },
                { key: 'ticket_time_added', label: 'Time Entry Logged' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`tech-${key}`}
                    checked={emailNotifTechEvents[key] !== false}
                    onChange={(e) => setEmailNotifTechEvents(prev => ({ ...prev, [key]: e.target.checked }))}
                    disabled={settingsLoading}
                    className="w-3.5 h-3.5 rounded border-slate-600 bg-slate-700 text-blue-500 focus:ring-blue-500"
                  />
                  <label htmlFor={`tech-${key}`} className="text-xs text-slate-400">{label}</label>
                </div>
              ))}
            </div>
          </div>

          {/* Save Button */}
          <div className="mt-4 flex items-center gap-3">
            <button
              onClick={handleSaveEmailNotifSettings}
              disabled={emailNotifSaving || settingsLoading}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                emailNotifSaving || settingsLoading
                  ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {emailNotifSaving ? 'Saving...' : 'Save Email Settings'}
            </button>

            {emailNotifSaved && (
              <span className="text-sm text-green-400">Saved!</span>
            )}

            {emailNotifError && (
              <span className="text-sm text-red-400">{emailNotifError}</span>
            )}
          </div>

          {/* Status */}
          <div className="mt-3 text-xs text-slate-500 space-y-1">
            <div>
              Master: {emailNotifEnabled ? (
                <span className="text-green-400">Enabled</span>
              ) : (
                <span className="text-slate-400">Disabled — no ticket emails are sent</span>
              )}
            </div>
            {emailNotifEnabled && (
              <>
                <div>
                  Customer emails: {emailNotifCustomerTestMode ? (
                    <span className="text-amber-400">Test Mode — routed to assigned tech (fallback: {emailNotifTestRecipient})</span>
                  ) : (
                    <span className="text-green-400">Live — sent to actual customers</span>
                  )}
                </div>
                <div>
                  Tech emails: {emailNotifTechTestMode ? (
                    <span className="text-amber-400">Test Mode — routed to {emailNotifTestRecipient}</span>
                  ) : (
                    <span className="text-green-400">Live — sent to actual techs</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Intake System Configuration */}
      <IntakeSystemSettings />

      {/* API Information */}
      <div className="bg-slate-800 rounded-lg border border-slate-700 p-4">
        <h3 className="text-sm font-semibold text-white mb-3">API Configuration</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-400">API Endpoint:</span>
            <span className="text-slate-300 font-mono text-xs">/api</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Environment:</span>
            <span className="text-slate-300 text-xs">{import.meta.env.MODE}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
